import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { fetchWithAuth } from '../util/fetchWithAuth';
import FullScreenLoader from '../components/FullScreenLoader';
import {
  PickUpPointDashboardAttentionGroup,
  PickUpPointDashboardData,
  PickUpPointDashboardRow,
} from '../types';

const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(2000, i, 1).toLocaleString('en-GB', { month: 'long' }),
}));

const formatDays = (days: number | null) => (days === null ? '-' : days.toFixed(1));

const formatWeek = (isoDate: string) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const parseExcluded = (raw: string | null) =>
  (raw || '')
    .split(',')
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0);

const SummaryCard = ({ title, value, detail, highlight = false }: { title: string; value: string | number; detail: string; highlight?: boolean }) => (
  <div className={`rounded-lg border p-4 flex flex-col gap-1 ${highlight ? 'bg-orange-50 border-orange-300' : 'bg-white border-gray-200'}`}>
    <span className={`text-sm ${highlight ? 'text-orange-800' : 'text-gray-600'}`}>{title}</span>
    <span className={`text-3xl font-bold ${highlight ? 'text-orange-800' : 'text-gray-900'}`}>{value}</span>
    <span className={`text-sm ${highlight ? 'text-orange-800' : 'text-gray-600'}`}>{detail}</span>
  </div>
);

const AttentionList = ({
  title,
  hint,
  badgeClass,
  group,
  renderDetail,
}: {
  title: string;
  hint: string;
  badgeClass: string;
  group: PickUpPointDashboardAttentionGroup;
  renderDetail: (order: PickUpPointDashboardAttentionGroup['orders'][number]) => React.ReactNode;
}) => (
  <div className="flex flex-col gap-2">
    <div className="flex items-center gap-2">
      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${badgeClass}`}>{title}</span>
      <span className="text-sm text-gray-600">{hint}</span>
    </div>
    {group.orders.length === 0 ? (
      <p className="border border-gray-100 rounded px-3 py-2 text-sm text-gray-600">None at the selected points.</p>
    ) : (
      <ul className="border border-gray-100 rounded divide-y divide-gray-100">
        {group.orders.map((order) => (
          <li key={order.id} className="px-3 py-2 text-sm flex flex-col gap-0.5">
            <div className="flex justify-between gap-2">
              <Link to={`/orders/${order.id}`} className="text-blue-600 hover:text-blue-800">
                {order.order_reference}
              </Link>
              <span className="text-gray-700">{order.pick_up_point}</span>
            </div>
            {renderDetail(order)}
          </li>
        ))}
        {group.total > group.orders.length && (
          <li className="px-3 py-2 text-sm text-gray-600">and {group.total - group.orders.length} more</li>
        )}
      </ul>
    )}
  </div>
);

const WeeklyChart = ({ point }: { point: PickUpPointDashboardRow }) => {
  const data = point.weekly.map((week) => ({
    week: formatWeek(week.week_start),
    Customer: week.customer,
    Admin: week.admin,
  }));

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-gray-700">{point.name}</h3>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="week" tick={{ fontSize: 12 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
          <Tooltip />
          <Legend />
          <Bar dataKey="Customer" stackId="orders" fill="#1d4ed8" />
          <Bar dataKey="Admin" stackId="orders" fill="#f59e0b" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const PickUpPointDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<PickUpPointDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const now = new Date();
  const month = Number(searchParams.get('month')) || now.getMonth() + 1;
  const year = Number(searchParams.get('year')) || now.getFullYear();
  const excludeParam = searchParams.get('exclude');
  const excluded = parseExcluded(excludeParam);
  const recentYears = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);
  const years = recentYears.includes(year) ? recentYears : [...recentYears, year].sort((a, b) => b - a);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      const query = new URLSearchParams({ month: String(month), year: String(year) });
      parseExcluded(excludeParam).forEach((id) => query.append('exclude[]', String(id)));
      try {
        const response = await fetchWithAuth(`/pick_up_points/dashboard?${query.toString()}`);
        if (cancelled) return;
        if (response.ok) {
          const body = await response.json();
          if (!cancelled) setData(body);
        } else {
          setData(null);
          setError('Could not load the dashboard. Check the month and year, then try again.');
        }
      } catch {
        if (!cancelled) {
          setData(null);
          setError('Could not load the dashboard. Check your connection and try again.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [month, year, excludeParam]);

  const updateParam = (key: string, value: string | null) => {
    setSearchParams((prev) => {
      if (value) prev.set(key, value);
      else prev.delete(key);
      return prev;
    });
  };

  const togglePoint = (id: number) => {
    const next = excluded.includes(id) ? excluded.filter((e) => e !== id) : [...excluded, id];
    updateParam('exclude', next.length ? next.join(',') : null);
  };

  const summary = data?.summary;
  const attentionTotal = data
    ? data.attention.overstaying.total + data.attention.missing_compartment.total + data.attention.excluded_from_bulk_update.total
    : 0;
  const includedCount = data?.pick_up_points.filter((p) => p.included).length ?? 0;

  if (isLoading && !data) {
    return <FullScreenLoader message={'Fetching metrics...'} />;
  }

  return (
    <div className="px-4 md:px-6 py-6 flex flex-col gap-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold text-gray-800">Pick-Up Point Dashboard</h1>
          <p className="text-sm text-gray-600">
            Live columns show today. Orders placed and collected follow the selected month. Test orders are excluded.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex flex-col gap-1">
            <label htmlFor="dashboardMonth" className="block text-sm font-medium text-gray-700">Month</label>
            <select
              id="dashboardMonth"
              value={month}
              onChange={(e) => updateParam('month', e.target.value)}
              className="px-3 py-2 border rounded bg-white"
            >
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="dashboardYear" className="block text-sm font-medium text-gray-700">Year</label>
            <select
              id="dashboardYear"
              value={year}
              onChange={(e) => updateParam('year', e.target.value)}
              className="px-3 py-2 border rounded bg-white"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {data && (
        <fieldset className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex flex-wrap items-center gap-3">
          <legend className="sr-only">Pick-up points</legend>
          <span className="text-sm font-semibold text-gray-800" aria-hidden="true">Pick-up points</span>
          {data.pick_up_points.map((point) => (
            <label
              key={point.id}
              htmlFor={`dashboardPoint${point.id}`}
              className={`flex items-center gap-2 min-h-[44px] px-3 rounded-full border text-sm cursor-pointer ${
                point.included ? 'border-blue-600 bg-blue-50 text-gray-900' : 'border-gray-300 bg-white text-gray-700'
              }`}
            >
              <input
                id={`dashboardPoint${point.id}`}
                type="checkbox"
                checked={point.included}
                onChange={() => togglePoint(point.id)}
                className="h-4 w-4"
              />
              {point.name}
              {point.default && <span className="text-xs text-gray-600">(head office)</span>}
            </label>
          ))}
          <span className="ml-auto text-sm text-gray-600">
            Showing {includedCount} of {data.pick_up_points.length} points
          </span>
        </fieldset>
      )}

      {error && <p role="alert" className="text-red-600 font-medium">{error}</p>}

      {data && includedCount === 0 && (
        <p className="border border-dashed border-gray-300 rounded-lg bg-white px-4 py-3 text-sm text-gray-700">
          No pick-up points selected. Tick at least one to see its metrics.
        </p>
      )}

      {data && summary && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <SummaryCard
              title="Orders placed"
              value={summary.orders_placed.total}
              detail={`${summary.orders_placed.customer} by customers, ${summary.orders_placed.admin} by admins`}
            />
            <SummaryCard title="Pre-ready" value={summary.pre_ready} detail="details set, items in Eswatini" />
            <SummaryCard title="Ready for collection" value={summary.ready_for_collection} detail="waiting on customers" />
            <SummaryCard
              title="Overstaying"
              value={summary.overstaying}
              detail={`ready over ${data.collection_overstay_days} days`}
              highlight={summary.overstaying > 0}
            />
            <SummaryCard
              title="Avg days to collect"
              value={formatDays(summary.avg_days_to_collect)}
              detail={`${summary.collected} collected this month`}
            />
          </div>

          <section className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Needs attention</h2>
              <span className="text-sm text-gray-600">{attentionTotal} {attentionTotal === 1 ? 'item' : 'items'}</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <AttentionList
                title="Overstaying"
                hint={`ready over ${data.collection_overstay_days} days`}
                badgeClass="bg-orange-100 text-orange-800"
                group={data.attention.overstaying}
                renderDetail={(order) => <span className="text-gray-600">{order.days_waiting} days waiting</span>}
              />
              <AttentionList
                title="No compartment"
                hint="blocks marking ready"
                badgeClass="bg-red-100 text-red-800"
                group={data.attention.missing_compartment}
                renderDetail={() => null}
              />
              <AttentionList
                title="Excluded from bulk"
                hint="parked with a reason"
                badgeClass="bg-gray-200 text-gray-700"
                group={data.attention.excluded_from_bulk_update}
                renderDetail={(order) => <span className="text-gray-600">{order.reason}</span>}
              />
            </div>
          </section>

          {data.points.length > 0 && (
            <section className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
              <h2 className="text-lg font-bold text-gray-800 px-4 md:px-6 pt-4 pb-3">By pick-up point</h2>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
                  <tr>
                    <th className="px-4 py-2">Point</th>
                    <th className="px-4 py-2">Orders placed</th>
                    <th className="px-4 py-2">Pre-ready</th>
                    <th className="px-4 py-2">Ready</th>
                    <th className="px-4 py-2">Overstaying</th>
                    <th className="px-4 py-2">Shelves used</th>
                    <th className="px-4 py-2">Collected</th>
                    <th className="px-4 py-2">Avg days</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.points.map((point) => (
                    <tr key={point.id}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Link to={`/pick-up-points/${point.id}`} className="font-semibold text-blue-600 hover:text-blue-800">
                            {point.name}
                          </Link>
                          {point.default && (
                            <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              Default
                            </span>
                          )}
                        </div>
                        {point.city && <div className="text-gray-600">{point.city}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{point.orders_placed.total}</div>
                        <div className="text-gray-600">
                          {point.orders_placed.customer} customer, {point.orders_placed.admin} admin
                        </div>
                      </td>
                      <td className="px-4 py-3">{point.pre_ready}</td>
                      <td className="px-4 py-3">{point.ready_for_collection}</td>
                      <td className="px-4 py-3">
                        {point.overstaying > 0 ? (
                          <span className="inline-block px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 font-semibold">
                            {point.overstaying}
                          </span>
                        ) : (
                          <span className="text-gray-600">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {point.shelves.total > 0 ? `${point.shelves.used} of ${point.shelves.total}` : <span className="text-gray-600">Not used</span>}
                      </td>
                      <td className="px-4 py-3">{point.collected}</td>
                      <td className="px-4 py-3">{formatDays(point.avg_days_to_collect)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {data.points.length > 0 && (
            <section className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 flex flex-col gap-4">
              <h2 className="text-lg font-bold text-gray-800">Orders placed per week</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {data.points.map((point) => (
                  <WeeklyChart key={point.id} point={point} />
                ))}
              </div>
              <p className="text-sm text-gray-600">
                Customer means the app or website. Admin means WhatsApp or the CRM. The default point&apos;s counts include orders that got it automatically.
              </p>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default PickUpPointDashboard;
